import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useMemo } from 'react'
import useSWR from 'swr'

export type NotificationStatus = 'unread' | 'read' | 'archived'

export interface Notification {
  id: string
  message: string
  title?: string
  body?: string
  status: NotificationStatus
  created_at: string
  delivered_at?: string
  isUnread: boolean
  cta?: {
    label: string
    url: string
  }
}

export interface ListNotificationsResponse {
  notifications: Notification[]
  total: number
  page: number
  page_size: number
}

type ServerNotificationItem = {
  id: string
  notification: {
    status: NotificationStatus
    created_at: string
    delivered_at?: string | null
    payload: {
      title?: string | null
      body?: string | null
      cta?: { label: string; url: string } | null
    }
  }
  rendered?: { inapp_rich_text?: string | null; html_email?: string | null }
}

type ServerListNotificationsResponse = {
  items: ServerNotificationItem[]
  page: number
  page_size: number
  total: number | null
}

interface UseNotificationsOptions {
  status?: NotificationStatus
  page?: number
  pageSize?: number
  enabled?: boolean
  unreadPageSize?: number
  unreadMaxPages?: number
}

const mapServerToClient = (
  server: ServerListNotificationsResponse
): ListNotificationsResponse => {
  const notifications: Notification[] = (server.items ?? []).map(
    ({ id, notification, rendered }) => {
      const fallback =
        (notification.payload?.title ?? '').trim() +
        (notification.payload?.body ? `\n\n${notification.payload.body}` : '')
      return {
        id,
        message: (rendered?.inapp_rich_text ?? fallback).trim(),
        title: notification.payload?.title ?? undefined,
        body: notification.payload?.body ?? undefined,
        status: notification.status,
        created_at: notification.created_at,
        delivered_at: notification.delivered_at ?? undefined,
        isUnread: notification.status === 'unread',
        cta: notification.payload?.cta ?? undefined,
      }
    }
  )
  return {
    notifications,
    total: server.total ?? notifications.length,
    page: server.page,
    page_size: server.page_size,
  }
}

export const useNotifications = (options: UseNotificationsOptions = {}) => {
  const { ALERTS_URL } = useRuntimeVariables()
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { user } = useGeckoPermissions()

  const {
    status,
    page = 1,
    pageSize = 25,
    enabled = true,
    unreadPageSize = 100,
    unreadMaxPages = 10,
  } = options

  const buildUrl = useCallback(
    (q: { status?: NotificationStatus; page: number; pageSize: number }) => {
      const params = new URLSearchParams()
      if (user?.id) params.append('user_id', user.id)
      if (q.status) params.append('status', q.status)
      params.append('page', String(q.page))
      params.append('page_size', String(Math.min(q.pageSize, 100)))
      return `${ALERTS_URL}/api/v1/notifications?${params.toString()}`
    },
    [ALERTS_URL, user?.id]
  )

  const fetchList = useCallback(
    async (url: string): Promise<ListNotificationsResponse> => {
      if (!isAuthenticated || !user?.id)
        throw new Error('User is not authenticated')
      const res = await axios.get<ServerListNotificationsResponse>(url, {
        headers: { Accept: 'application/json' },
      })
      return mapServerToClient(res.data)
    },
    [axios, isAuthenticated, user?.id]
  )

  /** Main (paginated) list for the panel */
  const listUrl = useMemo(
    () => buildUrl({ status, page, pageSize }),
    [buildUrl, status, page, pageSize]
  )

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    ListNotificationsResponse,
    Error
  >(enabled && isAuthenticated && user?.id ? listUrl : null, fetchList)

  /**
   * Unread count on page load: paginate with page_size<=100 until:
   * - server returns fewer than page_size items, or
   * - we hit unreadMaxPages (safety cap).
   */
  const unreadKey = useMemo(
    () => (enabled && isAuthenticated && user?.id ? `unread:${user.id}` : null),
    [enabled, isAuthenticated, user?.id]
  )

  const fetchUnreadCount = useCallback(async (): Promise<number> => {
    if (!isAuthenticated || !user?.id)
      throw new Error('User is not authenticated')

    let count = 0
    for (let p = 1; p <= unreadMaxPages; p++) {
      const url = buildUrl({
        status: 'unread',
        page: p,
        pageSize: unreadPageSize,
      })
      const res = await axios.get<ServerListNotificationsResponse>(url, {
        headers: { Accept: 'application/json' },
      })
      const mapped = mapServerToClient(res.data)
      count += mapped.notifications.length
      if (mapped.notifications.length < Math.min(unreadPageSize, 100)) break
    }
    return count
  }, [
    axios,
    buildUrl,
    isAuthenticated,
    unreadMaxPages,
    unreadPageSize,
    user?.id,
  ])

  const {
    data: unreadCountData,
    error: unreadError,
    isLoading: isLoadingUnread,
    isValidating: isValidatingUnread,
    mutate: mutateUnread,
  } = useSWR<number, Error>(unreadKey, fetchUnreadCount, {
    revalidateOnFocus: false,
  })

  const unreadCount = unreadCountData ?? 0

  const updateNotificationStatus = useCallback(
    async (notificationId: string, newStatus: NotificationStatus) => {
      if (!isAuthenticated || !user?.id) {
        throw new Error('User is not authenticated')
      }

      const previousData = data
      const previousUnreadCount = unreadCountData

      if (data) {
        mutate(
          {
            ...data,
            notifications: data.notifications.map((n) =>
              n.id === notificationId
                ? { ...n, status: newStatus, isUnread: newStatus === 'unread' }
                : n
            ),
          },
          false
        )
      }

      if (unreadCountData !== undefined) {
        const newCount =
          newStatus === 'read'
            ? Math.max(0, unreadCountData - 1)
            : newStatus === 'unread'
              ? unreadCountData + 1
              : unreadCountData
        mutateUnread(newCount, false)
      }

      try {
        await axios.patch(
          `${ALERTS_URL}/api/v1/notifications/${notificationId}`,
          { status: newStatus },
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          }
        )

        await Promise.all([mutate(), mutateUnread()])
      } catch (error) {
        if (previousData) mutate(previousData, false)
        if (previousUnreadCount !== undefined)
          mutateUnread(previousUnreadCount, false)
        throw error
      }
    },
    [
      ALERTS_URL,
      axios,
      isAuthenticated,
      user?.id,
      mutate,
      mutateUnread,
      data,
      unreadCountData,
    ]
  )

  return {
    notifications: data?.notifications ?? [],
    total: data?.total ?? 0,
    page: data?.page ?? page,
    pageSize: data?.page_size ?? pageSize,

    unreadCount,
    isLoadingUnread,
    isValidatingUnread,
    unreadError,

    isLoading,
    isValidating,
    error,

    mutate,
    mutateUnread,
    updateNotificationStatus,
  }
}
